/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Popover,
  TextField,
  Divider,
  useTheme,
  styled,
  IconButton,
} from '@mui/material';
import {
  LocalizationProvider,
  StaticDatePicker,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  startOfDay,
  endOfDay,
  isWithinInterval,
  isSameDay,
  isBefore,
  addMonths,
  isSameMonth,
} from 'date-fns';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' && prop !== 'isBetween' && prop !== 'isStart' && prop !== 'isEnd',
})<{
  isSelected?: boolean;
  isBetween?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
}>(({ theme, isSelected, isBetween, isStart, isEnd }) => ({
  ...(isBetween && {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
    borderRadius: 0,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.light,
    },
  }),
  ...(isStart && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isEnd && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
}));

const DateRangePicker: React.FC<DateRangePickerProps> = ({ startDate, endDate, onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempStart, setTempStart] = useState<Date | null>(startDate);
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate);
  const [viewDate, setViewDate] = useState<Date>(startOfMonth(subMonths(new Date(), 1)));

  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
  }, [startDate, endDate]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setViewDate(startOfMonth(subMonths(new Date(), 1)));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange(tempStart, tempEnd);
    handleClose();
  };

  const handleCancel = () => {
    setTempStart(startDate);
    setTempEnd(endDate);
    handleClose();
  };

  const handleClear = () => {
    setTempStart(null);
    setTempEnd(null);
  };

  const handleDateClick = (date: Date | null) => {
    if (!date) return;
    const selectedDate = startOfDay(date);

    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(selectedDate);
      setTempEnd(null);
    } else {
      // If we have a start date but no end date
      if (isSameDay(selectedDate, tempStart)) {
        setTempEnd(endOfDay(selectedDate));
      } else if (isBefore(selectedDate, tempStart)) {
        setTempStart(selectedDate);
        setTempEnd(null);
      } else {
        setTempEnd(endOfDay(selectedDate));
      }
    }
  };

  const handlePrevMonth = () => {
    setViewDate((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setViewDate((prev) => addMonths(prev, 1));
  };

  const renderPickerDay = (props: PickersDayProps<Date>) => {
    const isStart = tempStart ? isSameDay(props.day, tempStart) : false;
    const isEnd = tempEnd ? isSameDay(props.day, tempEnd) : false;
    const isSelected = isStart || isEnd;
    const isBetween =
      tempStart && tempEnd
        ? isWithinInterval(props.day, { start: tempStart, end: tempEnd })
        : false;

    return (
      <CustomPickersDay
        {...props}
        isSelected={isSelected}
        isBetween={isBetween}
        isStart={isStart}
        isEnd={isEnd}
        onDaySelect={(date: Date) => {
          handleDateClick(date);
          props.onDaySelect?.(date);
        }}
      />
    );
  };

  const setRelativeRange = (days: number) => {
    const today = new Date();
    setTempStart(startOfDay(subDays(today, days)));
    setTempEnd(endOfDay(today));
  };

  const setMonthToDate = () => {
    const today = new Date();
    setTempStart(startOfMonth(today));
    setTempEnd(endOfDay(today));
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const displayValue =
    startDate && endDate
      ? `${format(startDate, 'yyyy-MM-dd')} — ${format(endDate, 'yyyy-MM-dd')}`
      : 'Select Date Range';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px',
          padding: '8.5px 14px',
          cursor: 'pointer',
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            borderColor: theme.palette.text.primary,
          },
          minWidth: '250px',
        }}
      >
        <CalendarTodayIcon sx={{ mr: 1, fontSize: '20px', color: theme.palette.text.secondary }} />
        <Typography variant="body2">{displayValue}</Typography>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 'auto',
            maxWidth: '800px',
            borderRadius: '8px',
            boxShadow: theme.shadows[10],
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Calendars Row */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
              }}
            >
              <IconButton onClick={handlePrevMonth} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {format(viewDate, 'MMMM yyyy')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {format(addMonths(viewDate, 1), 'MMMM yyyy')}
                </Typography>
              </Box>
              <IconButton
                onClick={handleNextMonth}
                size="small"
                disabled={isSameMonth(addMonths(viewDate, 1), new Date())}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={null}
                  key={viewDate.toISOString()}
                  referenceDate={viewDate}
                  onChange={() => {}}
                  slots={{ day: renderPickerDay }}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: [] },
                    toolbar: { hidden: true },
                    calendarHeader: { sx: { display: 'none' } },
                  }}
                />
              </Box>
              <Box>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={null}
                  key={addMonths(viewDate, 1).toISOString()}
                  referenceDate={addMonths(viewDate, 1)}
                  onChange={() => {}}
                  slots={{ day: renderPickerDay }}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: [] },
                    toolbar: { hidden: true },
                    calendarHeader: { sx: { display: 'none' } },
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Manual Inputs */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                Start date
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={tempStart ? format(tempStart, 'yyyy/MM/dd') : ''}
                placeholder="YYYY/MM/DD"
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                End date
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={tempEnd ? format(tempEnd, 'yyyy/MM/dd') : ''}
                placeholder="YYYY/MM/DD"
                InputProps={{ readOnly: true }}
              />
            </Box>
          </Box>

          {/* Relative Ranges */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Auto-select range (Relative)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Choose from the range we&apos;ve selected for you.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                Past:
              </Typography>
              <Button size="small" onClick={() => setRelativeRange(1)}>
                1 Day
              </Button>
              <Button size="small" onClick={() => setRelativeRange(7)}>
                7 Days
              </Button>
              <Button size="small" onClick={setMonthToDate}>
                Month to Date
              </Button>
              <Button size="small" onClick={() => setRelativeRange(30)}>
                1 Month
              </Button>
              <Button size="small" onClick={() => setRelativeRange(90)}>
                3 Months
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleClear} color="error" sx={{ mr: 'auto' }}>
              Clear
            </Button>
            <Button onClick={handleCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleApply} variant="contained" sx={{ borderRadius: '20px', px: 4 }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
